import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { PRESS_IMAGES } from '../data/press';

export const CROP_SIZES = [
  { w: 300,  h: 200,  name: '300x200'   },
  { w: 400,  h: 294,  name: '400x294'   },
  { w: 480,  h: 720,  name: '480x720'   },
  { w: 600,  h: 900,  name: '600x900'   },
  { w: 868,  h: 620,  name: '868x620'   },
  { w: 1024, h: 512,  name: '1024x512'  },
  { w: 1080, h: 1080, name: '1080x1080' },
  { w: 1200, h: 315,  name: '1200x315'  },
  { w: 1300, h: 512,  name: '1300x512'  },
  { w: 1300, h: 576,  name: '1300x576'  },
  { w: 1376, h: 400,  name: '1376x400'  },
  { w: 1440, h: 580,  name: '1440x580'  },
  { w: 1500, h: 990,  name: '1500x990'  },
  { w: 1600, h: 1200, name: '1600x1200' },
  { w: 1800, h: 472,  name: '1800x472'  },
  { w: 1920, h: 1080, name: '1920x1080' }
];

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/*
  Draws a “cover” crop of `img` at (targetW, targetH), centring the
  crop around a normalised focus point (0..1). Falls back to centre if
  no focus supplied.
*/
function cropCanvas(img, targetW, targetH, focus = { x: 0.5, y: 0.5 }) {
  const sw = img.naturalWidth;
  const sh = img.naturalHeight;
  const srcRatio = sw / sh;
  const dstRatio = targetW / targetH;

  let cropW, cropH, cropX, cropY;
  if (srcRatio > dstRatio) {
    // Source wider than target — crop horizontally, keep full height
    cropH = sh;
    cropW = sh * dstRatio;
    cropX = Math.max(0, Math.min(sw - cropW, (sw - cropW) * focus.x));
    cropY = 0;
  } else {
    // Source taller/narrower than target — crop vertically
    cropW = sw;
    cropH = sw / dstRatio;
    cropX = 0;
    cropY = Math.max(0, Math.min(sh - cropH, (sh - cropH) * focus.y));
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, targetW, targetH);
  return canvas;
}

function canvasToBlob(canvas, type = 'image/png') {
  return new Promise((resolve) => canvas.toBlob(resolve, type, 0.92));
}

// Yield to the event loop so the browser can repaint the progress UI
const yieldToBrowser = () =>
  new Promise((r) => (window.requestIdleCallback ? window.requestIdleCallback(r, { timeout: 40 }) : setTimeout(r, 0)));

export async function generateCropsPack(onProgress) {
  const zip = new JSZip();
  const root = zip.folder('ASH-Image-Crops-Pack');

  const sources = PRESS_IMAGES.filter((p) => !p.keyArt);
  const total = sources.length * CROP_SIZES.length;
  let done = 0;

  root.file(
    'README.txt',
    [
      'ASH — THE WITCH TRIALS OF DRUDENHAUS',
      'IMAGE CROP PACK',
      '',
      'Every production still delivered in the sizes commonly required by',
      'ticketing platforms, listings sites and social channels.',
      '',
      'File naming: <base-image>-<width>x<height>.png',
      '',
      'Sizes included:',
      ...CROP_SIZES.map((s) => `  • ${s.name} — ${s.w}×${s.h}px`),
      '',
      'Source images:',
      ...sources.map(
        (p) => `  • ${p.file.split('/').pop()} — ${p.caption}`
      ),
      '',
      'All images © Alex Walton for COLAB Theatre.',
      'Free to use for editorial and promotional coverage of ASH.',
      'Please credit "Alex Walton for COLAB Theatre".',
      ''
    ].join('\n')
  );

  for (const p of sources) {
    const baseName = p.file
      .split('/')
      .pop()
      .replace(/\.(png|jpe?g)$/i, '');
    const folder = root.folder(baseName);
    const img = await loadImage(p.file);
    const focus = p.focus || { x: 0.5, y: 0.5 };

    for (const size of CROP_SIZES) {
      const canvas = cropCanvas(img, size.w, size.h, focus);
      const blob = await canvasToBlob(canvas, 'image/png');
      folder.file(`${baseName}-${size.name}.png`, blob);
      done += 1;
      if (onProgress) onProgress({ done, total, current: `${baseName} — ${size.name}` });
      // Give the browser a chance to breathe between draws
      // (keeps the progress UI responsive)
      // eslint-disable-next-line no-await-in-loop
      await yieldToBrowser();
    }
  }

  const content = await zip.generateAsync(
    { type: 'blob' },
    (meta) => {
      if (onProgress) onProgress({ done: total, total, current: 'Compressing…', zip: meta.percent });
    }
  );
  saveAs(content, 'ASH-Image-Crops-Pack.zip');
}
